import { Stack, Typography } from '@mui/material';

import { useAuth } from '@/auth/useAuth';
import { ErrorState } from '@/components/common/ErrorState';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { PageHeader } from '@/components/common/PageHeader';
import { ROLES } from '@/constants/roles';
import { useWorkspace } from '@/features/company-portal/workspace/WorkspaceProvider';
import { CandidateOptInCard } from '@/features/identity/components/CandidateOptInCard';
import { ProfileCard } from '@/features/identity/components/ProfileCard';
import { useProfile } from '@/features/identity/hooks/useProfile';

/**
 * `/account/profile` — a single-entity fetch, so only loading/error/populated states apply; there
 * is no "empty" case for "your own profile" the way there is for a list (docs/development/FrontendGuidelines.md
 * §7.10 quartet doesn't include an empty state where the concept doesn't apply).
 *
 * The Candidate opt-in lives here too: it is role-agnostic for every OTHER persona (company member,
 * staff, or otherwise) — it's the ONE page every such account can always reach regardless of which
 * workspace it already resolved to (`RootRedirect`/`OnboardingPage` never re-offer the onboarding
 * chooser once a journey exists) — hidden once the account already holds the Candidate persona.
 * Platform Admin is the one exception: it's an internal operating role, never a job seeker, so the
 * card never applies there regardless of the Candidate-workspace check.
 */
export function ProfilePage() {
  const { user, isLoading, error, refetch } = useProfile();
  const workspace = useWorkspace();
  const { hasRole } = useAuth();

  return (
    <Stack spacing={3}>
      <PageHeader title="Profile" />
      {isLoading ? <LoadingSpinner label="Loading your profile…" /> : null}
      {!isLoading && error ? <ErrorState message={error} onRetry={refetch} /> : null}
      {!isLoading && !error && user ? (
        <>
          <ProfileCard user={user} />
          {!workspace.loading && !workspace.hasCandidateWorkspace && !hasRole(ROLES.Admin) ? (
            <CandidateOptInCard />
          ) : null}
        </>
      ) : null}
      {!isLoading && !error && !user ? (
        <Typography color="text.secondary">Unable to load your profile right now.</Typography>
      ) : null}
    </Stack>
  );
}
